import { tooltipClasses } from '@mui/material';
import { type Theme } from '@mui/material/styles';
import { type RefObject } from 'react';

/** A rect as Popper hands it to an offset, in page pixels. */
interface PopperRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

/**
 * Where a tooltip placed to the right of `anchor` moves, as Popper's
 * offset `[along the side, away from the anchor]`, so that it opens at the
 * right edge of `panel`, its top level with the panel's top, rather than
 * over the panel. Zero for a tooltip on another side, or for an anchor
 * outside the panel.
 */
export function offsetBesidePanel(
    placement: string,
    anchor: PopperRect,
    tooltip: PopperRect,
    panel: DOMRect | undefined
): [number, number] {
    if (!panel || !placement.startsWith('right')) {
        return [0, 0];
    }
    // Popper measures in page pixels, the panel's rect is in the window's.
    const left = panel.left + window.scrollX;
    const right = panel.right + window.scrollX;
    const top = panel.top + window.scrollY;
    const anchorRight = anchor.x + anchor.width;
    if (anchor.x < left || anchorRight > right) {
        return [0, 0];
    }
    // Where Popper puts the tooltip's top before the offset.
    const placedTop = placement.endsWith('-start')
        ? anchor.y
        : placement.endsWith('-end')
          ? anchor.y + anchor.height - tooltip.height
          : anchor.y + anchor.height / 2 - tooltip.height / 2;
    return [top - placedTop, right - anchorRight];
}

/**
 * `outer` with tooltips that open to the right of the panel `panelRef`
 * holds, their top level with the panel's top and as far from its edge as
 * a menu opens from its button, so they do not cover the panel. A tooltip
 * that names its own placement keeps its side.
 */
export function tooltipsBesidePanel(
    outer: Theme,
    panelRef: RefObject<HTMLElement | null>
): Theme {
    const outerTooltipStyles = outer.components?.MuiTooltip?.styleOverrides;
    const outerTooltip = outerTooltipStyles?.tooltip;
    return {
        ...outer,
        components: {
            ...outer.components,
            MuiTooltip: {
                ...outer.components?.MuiTooltip,
                defaultProps: {
                    ...outer.components?.MuiTooltip?.defaultProps,
                    placement: 'right',
                    PopperProps: {
                        popperOptions: {
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                        offset: ({
                                            placement,
                                            reference,
                                            popper,
                                        }: {
                                            placement: string;
                                            reference: PopperRect;
                                            popper: PopperRect;
                                        }) =>
                                            offsetBesidePanel(
                                                placement,
                                                reference,
                                                popper,
                                                panelRef.current?.getBoundingClientRect()
                                            ),
                                    },
                                },
                            ],
                        },
                    },
                },
                styleOverrides: {
                    ...outerTooltipStyles,
                    // Replaces MUI's 14 px from the anchor with the menus' gap.
                    tooltip: (props) => [
                        typeof outerTooltip === 'function'
                            ? outerTooltip(props)
                            : outerTooltip,
                        {
                            [`.${tooltipClasses.popper}[data-popper-placement*="right"] &`]:
                                { marginLeft: props.theme.spacing(0.5) },
                        },
                    ],
                },
            },
        },
    };
}
